import { attach, sample } from 'effector';

import { ProfileAddress } from '@domains/addresses';
import { ProfileWholesaleAddress } from '@domains/profile';
import { Seo } from '@domains/seo';
import { User, UserRoleId } from '@domains/user';

import { $commonData } from '@stores/global-data';
import { errorNotificationFx } from '@stores/helpers';
import { rootDomain } from '@stores/root';

import { ApiError } from '@api/client/errors';
import {
    apiAddressesCreate,
    apiAddressesDelete,
    apiAddressesList,
    apiAddressesUpdate,
    apiProfile,
    apiProfileUpdate,
    apiWholesaleAddresses,
} from '@api/methods';
import {
    AddressesCreatePayload,
    AddressesCreateResult,
    AddressesDeletePayload,
    AddressesDeleteResult,
    AddressesListPayload,
    AddressesListResult,
    AddressesUpdatePayload,
    AddressesUpdateResult,
    ProfilePayload,
    ProfileResult,
    ProfileUpdatePayload,
    ProfileUpdateResult,
    WholesaleAddressesPayload,
    WholesaleAddressesResult,
} from '@api/protocol';

import { isValueSet } from '@utils/guards';
import { defaultSeoState } from '@utils/seo';

import { setIsOpenAddAddress } from './dialogs';

export type ProfileState = {
    user: User | null;
    wholesalerAddresses: ProfileWholesaleAddress[];
    addresses: ProfileAddress[];
    editedAddress: ProfileAddress | null;
    defaultCitySelectedName: string;
    seo: Seo;
};

// --- Domain ---
const profileDomain = rootDomain.createDomain();

// --- Effects ---
const _getProfileFx = profileDomain
    .createEffect<ProfilePayload, ProfileResult, ApiError>()
    .use(apiProfile);

const _getWholesaleAddressesFx = profileDomain
    .createEffect<WholesaleAddressesPayload, WholesaleAddressesResult, ApiError>()
    .use(apiWholesaleAddresses);

const _updateProfileFx = profileDomain
    .createEffect<ProfileUpdatePayload, ProfileUpdateResult, ApiError>()
    .use(apiProfileUpdate);

export const getAddressesFx = profileDomain
    .createEffect<AddressesListPayload, AddressesListResult>()
    .use(apiAddressesList);

export const createAddressesFx = profileDomain
    .createEffect<AddressesCreatePayload, AddressesCreateResult>()
    .use(apiAddressesCreate);

export const updateAddressesFx = profileDomain
    .createEffect<AddressesUpdatePayload, AddressesUpdateResult>()
    .use(apiAddressesUpdate);

export const deleteAddressesFx = profileDomain
    .createEffect<AddressesDeletePayload, AddressesDeleteResult>()
    .use(apiAddressesDelete);

// --- Attached Effects ---
export const getProfileFx = attach({ effect: _getProfileFx });
const localGetProfileFx = attach({ effect: _getProfileFx });
export const getWholesaleAddressesFx = attach({ effect: _getWholesaleAddressesFx });
export const updateProfileFx = attach({ effect: _updateProfileFx });

// --- Events ---
export const setUser = profileDomain.createEvent<User | null>();
export const setEditedAddress = profileDomain.createEvent<ProfileAddress | null>();
export const setDefaultCityNameSelected =
    profileDomain.createEvent<ProfileState['defaultCitySelectedName']>();
export const getUserData = profileDomain.createEvent();

const profileInitialState: ProfileState = {
    user: null,
    wholesalerAddresses: [],
    addresses: [],
    editedAddress: null,
    defaultCitySelectedName: 'Владивосток',
    seo: defaultSeoState,
};

export const $profile = profileDomain
    .createStore<ProfileState>(profileInitialState, { name: 'profile' })
    .on([getProfileFx.doneData, localGetProfileFx.doneData], (state, data) => ({
        ...state,
        user: data.user,
        seo: data.seo,
    }))
    .on(getWholesaleAddressesFx.doneData, (state, data) => ({
        ...state,
        wholesalerAddresses: data.items,
    }))
    .on(updateProfileFx.doneData, (state, data) => ({ ...state, user: data.user }))
    .on(getAddressesFx.doneData, (state, data) => ({ ...state, addresses: data.items }))
    .on(createAddressesFx.doneData, (state, data) => ({ ...state, addresses: data.items }))
    .on(updateAddressesFx.doneData, (state, data) => ({ ...state, addresses: data.items }))
    .on(setUser, (state, data) => ({ ...state, user: data }))
    .on(setDefaultCityNameSelected, (state, data) => ({ ...state, defaultCitySelectedName: data }))
    .on(setEditedAddress, (state, data) => ({ ...state, editedAddress: data }))
    .on(setIsOpenAddAddress, (state) => ({ ...state, editedAddress: null }));

export const $isLoggedIn = $profile.map<boolean>(({ user }) => isValueSet(user));
export const $isWholesale = $profile.map<boolean>(
    (state) => state.user?.role.id === UserRoleId.Wholesale,
);
export const $hasUserName = $profile.map<boolean>(
    ({ user }) => isValueSet(user) && isValueSet(user.name) && user.name.length > 0,
);
export const $hasUserEmail = $profile.map<boolean>(
    ({ user }) => isValueSet(user) && isValueSet(user.email) && user.email.length > 0,
);

// --- Samples ---
sample({
    clock: $commonData,
    filter: (store) => isValueSet(store.user),
    fn: (store) => store.user,
    target: setUser,
});

sample({
    clock: getUserData,
    target: localGetProfileFx,
});

sample({
    clock: localGetProfileFx.failData,
    target: errorNotificationFx,
});
